/**
 * Customer order tracking helpers (pure). A multi-kitchen order is one parent_orders doc plus one
 * orders doc per kitchen; tracking shows one status timeline per kitchen order.
 */
export const TRACK_STEPS = ["Placed", "Preparing", "Ready", "On the way", "Delivered"] as const;

/** Index into TRACK_STEPS for a kitchen-order status (-1 = cancelled / not on the timeline). */
export function trackStep(status: string): number {
  switch (String(status || "")) {
    case "awaiting_payment": case "awaiting_chef_confirmation":
    case "pending": case "placed": case "new": return 0;
    case "confirmed": case "accepted": case "preparing": return 1;
    case "ready": return 2;
    case "out_for_delivery": return 3;
    case "delivered": case "settled": return 4;
    default: return -1;
  }
}

export function kitchenStatusLabel(o: { status?: string; selfDelivery?: boolean; selfDeliveredByName?: string }): string {
  const st = String(o.status || "");
  switch (st) {
    case "awaiting_payment": return "Waiting for payment";
    case "awaiting_chef_confirmation": return "Waiting for the kitchen to confirm";
    case "pending": case "placed": case "new": return "Order placed";
    case "confirmed": case "accepted": case "preparing": return "Being prepared";
    case "ready": return "Packed, waiting for pickup";
    case "out_for_delivery": {
      if (!o.selfDelivery) return "Out for delivery";
      const who = String(o.selfDeliveredByName || "").trim();
      return who && who !== "Kitchen" ? `On the way: ${who} is delivering it` : "On the way: the kitchen is delivering it";
    }
    case "delivered": case "settled": return "Delivered";
    case "cancelled": return "Cancelled";
    case "complaint_raised": return "Issue reported";
    default: return st ? st.replace(/_/g, " ") : "Unknown";
  }
}

/** Summary across kitchens (mirrors functions/src/orderTrackingCore aggregateParentStatus). */
export function overallStatus(statuses: string[]): string {
  const live = statuses.filter((s) => !["cancelled", "rejected", "failed"].includes(s));
  if (statuses.length === 0) return "pending";
  if (live.length === 0) return "cancelled";
  const steps = live.map(trackStep);
  if (steps.every((x) => x >= 4)) return "delivered";
  if (steps.some((x) => x >= 3)) return "out_for_delivery";
  let min = live[0];
  for (const s of live) if (trackStep(s) < trackStep(min)) min = s;
  return min;
}

/** Show the delivery-partner code only when a delivery partner (not the chef) is on the way. */
export function showDeliveryOtp(o: { status?: string; selfDelivery?: boolean; deliveryOtp?: string }): boolean {
  return o.status === "out_for_delivery" && !o.selfDelivery && !!o.deliveryOtp;
}

/** Route for tracking: always the parent id when there is one (so all kitchens show together). */
export function trackPath(o: { id: string; parentOrderId?: string | null }): string {
  return `/orders/${encodeURIComponent(o.parentOrderId || o.id)}`;
}
